import * as React from 'react';
import styled from 'styled-components';
import { fieldValidation, ExternalUser, ActionPointData, PublicActionPlanLink } from '../../amorg-common';
import { Button } from '../../designSystem/button/Button';
import { EditableText } from '../../designSystem/compositeComponents/EditableText';
import { EditableTextArea } from '../../designSystem/compositeComponents/EditableTextArea';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { Icon, iconName } from '../../designSystem/icons/Icons';
import { DateInput } from '../../designSystem/inputs/DateInput';
import { OnOff } from '../../designSystem/inputs/OnOff';
import { ActionPlanLinkEditor } from '../../pages/actionplans/components/ActionPlanLinkEditor';
import { ProtectedPageContext } from '../../pages/_contexts/ProtectedPageContext';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { updateActionPoint } from '../../api/actionPointClientApi';

export type ActionPointMode = 
    { type: 'actionPlanTopic', topicId: string;} | 
    { type: 'agendaItem', agendaItemId: string; };

type ActionPointRowProps = {
    mode:ActionPointMode,
    actionPoint:ActionPointData,
    disabled:boolean,
    linkDisplay?:{
        siteId?:string,
        actionPlanId:string,
        links:PublicActionPlanLink[]
    },
    onUpdated: () => void,
    onDelete:() => void,
    onSelectUser:() => void
}
export function ActionPointRow(props:ActionPointRowProps):JSX.Element
{
    const [loading, setLoading] = React.useState(false);

    const colors = React.useContext(ProtectedPageContext).colors;

    const ap = props.actionPoint;

    let icon: { name: iconName, color: string; } = { name: 'navigation_clock', color: colors.icon.passive };
    if (ap.done)
        icon = { name: 'status_ok', color: colors.icon.success };
    else if (!ap.done && ap.deadline && new Date(ap.deadline) < new Date())
        icon = { name: 'status_error', color: colors.icon.error };

    
    
    async function handleUpdateActionPoint (ap: ActionPointData)
    {
        setLoading(true);
        await updateActionPoint(ap.id, ap.description, ap.assignedUser, ap.deadline, ap.done);
        setLoading(false);

        props.onUpdated();
    }

    return (
        <StyledActionPointContainer>
            
            <LoadingPageOverlay loading={loading} />

            <StyledActionPointText>
                <Flex alignItems="center" gap={8} style={{ flexGrow: 1 }}>

                    <Icon name={icon.name} color={icon.color} />

                    <EditableTextArea
                        disabled={props.disabled}
                        placeholder=""
                        ariaLabel={"Handlepunkt beskrivelse"}
                        value={ap.description}
                        hideHoverEditButton
                        onSave={(value, isValid) => isValid && handleUpdateActionPoint({ ...ap, description: value })}
                        validator={(value) => fieldValidation.actionPoint.description(value)
                            ? { valid: true }
                            : { valid: false, errorMessage: `Skal udfyldes. Må ikke overstige 200 (${value.length}) tegn.` }
                        }
                    />
                </Flex>
            </StyledActionPointText>


            <StyledActionPointOwner>
                {props.mode.type === 'actionPlanTopic' &&
                    <Flex style={{ width: '240px' }}>
                        <Icon name="interface_user_user" />
                        <div style={{ flexGrow: 1 }}>
                            <EditableText
                                fontSize={14}
                                disabled={props.disabled}
                                ariaLabel={"Vælg ejer for handlepunkt"}
                                value={ap.assignedUser === null ? '' : (ap.assignedUser as ExternalUser).fullName}
                                placeholder={"Vælg ejer"}
                                hideHoverEditButton
                                onSave={(value, isValid) =>
                                {
                                    isValid && handleUpdateActionPoint({
                                        ...ap,
                                        assignedUser: value === ''
                                            ? null
                                            : { type: 'external', email: '', fullName: value, role: '' }
                                    });
                                }}
                                validator={(value) => value === '' || fieldValidation.actionPoint.assignedExternalUserName(value)
                                    ? { valid: true }
                                    : { valid: false, errorMessage: `Ugyldigt navn. Maks. 50 tegn.` }
                                }
                            />
                        </div>

                    </Flex>
                }
                {props.mode.type === 'agendaItem' &&
                    <Flex style={{ width: '240px' }}>
                        <Icon name="interface_user_user" />
                        <div style={{ flexGrow: 1 }}>
                            <Button variant="clean"
                                text={ap.assignedUser === null ? 'Vælg ejer' : ap.assignedUser.fullName}
                                disabled={props.disabled}
                                onClick={() => props.onSelectUser()}
                            />
                        </div>
                    </Flex>
                }
            </StyledActionPointOwner>

            <StyledActionPointDeadline>
                <Icon name="navigation_calendar" />

                <div style={{ width: '170px' }}>
                    <DateInput noBorder
                        disabled={props.disabled}
                        placeHolder="Vælg deadline"
                        value={ap.deadline}
                        onValueChange={(value: Date | null) => handleUpdateActionPoint({ ...ap, deadline: value })}
                        showClearButton
                        noIcon
                        fontSize={14}
                        width="140px"
                    />
                </div>
                <OnOff size="medium" disabled={props.disabled} active={ap.done} onClick={(active) => handleUpdateActionPoint({ ...ap, done: active })} />
            </StyledActionPointDeadline>
            


            <StyledActionPointButtons>
                
                    <Flex flexDirection="row" justifyContent="flex-end" alignItems="center" gap={0}>
                        <Button variant="destructive-text" icon="interface_actions_delete" disabled={props.disabled} onClick={() => props.onDelete && props.onDelete()} />
                        {props.linkDisplay &&
                            <ActionPlanLinkEditor
                                siteId={props.linkDisplay.siteId}
                                actionPlanId={props.linkDisplay.actionPlanId}
                                linkContext={{ type: 'actionPoint', actionPointId: ap.id }}
                                link={props.linkDisplay.links.find(l => l.configuration.type === 'actionPoint' && l.configuration.actionPointId === ap.id)}
                                onUpdated={props.onUpdated}
                            />
                        }
                    </Flex>
                

            </StyledActionPointButtons>

        </StyledActionPointContainer>
    );
}

const customBreakpoint = 1400;

const StyledActionPointContainer = styled.div`
    display: grid;
    grid-template-areas: 
        "text text buttons"
        "owner deadline buttons";
    grid-template-columns: 1fr 1fr 100px;
    
    

    @media(min-width: ${customBreakpoint}px){
        grid-template-areas: "text owner deadline buttons";
        grid-template-columns: 1fr 250px 250px 100px;
    
    }

`;

const StyledActionPointText = styled.div`
    display: flex;
    align-items: center;
    border: ${props => props.theme.styles.card.variants.light.default.border};
    border-bottom: 0;
    border-right: 0;
    border-top-left-radius: ${props => props.theme.styles.card.borderRadius};
    
    padding: 8px 16px;
    grid-area: text;
    
    @media(min-width: ${customBreakpoint}px){
        border-bottom: ${props => props.theme.styles.card.variants.light.default.border};
        border-bottom-left-radius: ${props => props.theme.styles.card.borderRadius};
    }

`;

const StyledActionPointOwner = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    border-left: ${props => props.theme.styles.card.variants.light.default.border};
    border-bottom: ${props => props.theme.styles.card.variants.light.default.border};
    border-bottom-left-radius: ${props => props.theme.styles.card.borderRadius};
    padding: 8px 16px;
    grid-area: owner;

    @media(min-width: ${customBreakpoint}px){
        border-left:0;
        border-top: ${props => props.theme.styles.card.variants.light.default.border};
        border-bottom-left-radius: 0;
    }
`;

const StyledActionPointDeadline = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: ${props => props.theme.styles.card.variants.light.default.border};
    padding: 8px 16px;
    grid-area: deadline;

    @media(min-width: ${customBreakpoint}px){
        border-top: ${props => props.theme.styles.card.variants.light.default.border};
    
    }
`;

const StyledActionPointButtons = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    grid-area: buttons;
    border: ${props => props.theme.styles.card.variants.light.default.border};

    border-top-right-radius: ${props => props.theme.styles.card.borderRadius};
    border-bottom-right-radius: ${props => props.theme.styles.card.borderRadius};
`;